import type { Pattern } from '../pipeline/pipeline';
import type { CellEdit, Stroke } from './types';

/** Retourne l'index de palette de la cellule (col, row), ou undefined si hors-bornes. */
export function getCellAt(pattern: Pattern, col: number, row: number): number | undefined {
  if (col < 0 || col >= pattern.width || row < 0 || row >= pattern.height) return undefined;
  return pattern.cells[row * pattern.width + col];
}

/**
 * Retourne un nouveau `Pattern` avec la cellule (col, row) peinte avec `newIdx`.
 * Met à jour `counts` (décrément ancienne couleur, incrément nouvelle).
 *
 * - Si la cellule est déjà à `newIdx`, retourne le pattern inchangé (référence).
 * - **Throw** si la cellule est hors-bornes.
 */
export function paintCell(pattern: Pattern, col: number, row: number, newIdx: number): Pattern {
  if (col < 0 || col >= pattern.width || row < 0 || row >= pattern.height) {
    throw new Error(
      `paintCell : cellule (${col}, ${row}) hors bornes de la grille ${pattern.width}×${pattern.height}.`,
    );
  }
  const i = row * pattern.width + col;
  const oldIdx = pattern.cells[i];
  if (oldIdx === undefined) {
    throw new Error(`paintCell : cellule (${col}, ${row}) absente dans cells[].`);
  }
  if (oldIdx === newIdx) return pattern;

  const newCells = pattern.cells.slice();
  newCells[i] = newIdx;

  const newCounts = new Map(pattern.counts);
  const oldColorId = pattern.paletteColorIds[oldIdx];
  const newColorId = pattern.paletteColorIds[newIdx];
  if (oldColorId !== undefined) {
    newCounts.set(oldColorId, Math.max(0, (newCounts.get(oldColorId) ?? 0) - 1));
  }
  if (newColorId !== undefined) {
    newCounts.set(newColorId, (newCounts.get(newColorId) ?? 0) + 1);
  }

  return { ...pattern, cells: newCells, counts: newCounts };
}

/** Applique un `CellEdit` (peint `newIdx` à (col, row)). */
export function applyEdit(pattern: Pattern, edit: CellEdit): Pattern {
  return paintCell(pattern, edit.col, edit.row, edit.newIdx);
}

/** Inverse un `CellEdit` (peint `oldIdx` à (col, row)). Utilisé pour undo. */
export function revertEdit(pattern: Pattern, edit: CellEdit): Pattern {
  return paintCell(pattern, edit.col, edit.row, edit.oldIdx);
}

/** Applique tous les edits d'un stroke dans l'ordre (chronologique). */
export function applyStroke(pattern: Pattern, stroke: Stroke): Pattern {
  return paintCellsBatch(pattern, stroke);
}

/**
 * Applique plusieurs `CellEdit` à un pattern en **une seule copie** de `cells[]`
 * et `counts` (au lieu de N copies via N appels successifs à `paintCell`).
 *
 * Gain critique sur les gros drags : un stroke de 200 cellules sur 90k cells
 * passe de 200 × O(N) = O(N²/scale) à O(N) (un seul slice + Map clone, puis
 * mutations in-place sur les copies).
 *
 * Sémantique équivalente à `applyEdit` × N en chaîne :
 *  - si `newCells[i] === edit.newIdx` après les edits précédents, skip
 *  - met à jour `counts` au fil de l'eau (dec ancienne / inc nouvelle)
 *
 * @throws si une cellule est hors-bornes (toutes les edits doivent être valides).
 */
export function paintCellsBatch(pattern: Pattern, edits: ReadonlyArray<CellEdit>): Pattern {
  if (edits.length === 0) return pattern;

  const newCells = pattern.cells.slice();
  const newCounts = new Map(pattern.counts);
  let changed = false;

  for (const edit of edits) {
    const { col, row, newIdx } = edit;
    if (col < 0 || col >= pattern.width || row < 0 || row >= pattern.height) {
      throw new Error(
        `paintCellsBatch : cellule (${col}, ${row}) hors bornes de la grille ${pattern.width}×${pattern.height}.`,
      );
    }
    const i = row * pattern.width + col;
    const currentIdx = newCells[i];
    if (currentIdx === undefined || currentIdx === newIdx) continue;

    newCells[i] = newIdx;
    changed = true;

    const oldColorId = pattern.paletteColorIds[currentIdx];
    const newColorId = pattern.paletteColorIds[newIdx];
    if (oldColorId !== undefined) {
      newCounts.set(oldColorId, Math.max(0, (newCounts.get(oldColorId) ?? 0) - 1));
    }
    if (newColorId !== undefined) {
      newCounts.set(newColorId, (newCounts.get(newColorId) ?? 0) + 1);
    }
  }

  if (!changed) return pattern;
  return { ...pattern, cells: newCells, counts: newCounts };
}

/** Revert un stroke en ordre inverse (LIFO) — préserve l'état pré-stroke. */
export function revertStroke(pattern: Pattern, stroke: Stroke): Pattern {
  let p = pattern;
  for (let i = stroke.length - 1; i >= 0; i--) {
    const e = stroke[i];
    if (e === undefined) continue;
    p = revertEdit(p, e);
  }
  return p;
}
